import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { CheckCircle2 } from 'lucide-react'
import { Modal } from './ui/Modal'
import { Button } from './ui/Button'
import { TextField } from './ui/Field'
import { PhoneInputField, validatePhone } from './ui/PhoneInputField'
import {
  companyNameSchema,
  emailSchema,
  nameSchema,
} from '../lib/validators'
import { Select } from './ui/Select'
import { CountryFilter } from './ui/CountryFilter'
import { api, extractApiError } from '../lib/api'
import { plansResponse } from '../lib/types'
import { CopyableUrl } from './TenantRowActions'

/**
 * Platform Admin → "Create Company" flow.
 *
 * Two-step modal:
 *   1. Form — organisation name + owner full name + email + contact +
 *      plan. Field structure mirrors the self-signup form
 *      (`SignUpPage`) so the two creation paths feel identical to
 *      anyone who has used either. The only differences are:
 *        - Owner doesn't pick a password here — they set it through
 *          the emailed setup link.
 *        - Plan is admin-selected; Company Admins switch their own
 *          tenant's timezone from inside the app post-signup, so the
 *          admin doesn't pick one when creating the tenant.
 *   2. Success — confirms the email was queued and surfaces the
 *      one-time setup URL so the admin can hand it over directly if
 *      the email is delayed. The URL is the SAME `/reset-password?token=`
 *      link the email contains; we reuse PasswordResetToken on the
 *      backend to avoid a parallel table.
 */
const schema = z.object({
  // Shared validators — match Sign-up / Members / Profile so the
  // two creation paths (self-signup vs Platform-Admin-creates-tenant)
  // present the same wording for the same field.
  organisationName: companyNameSchema('Company Name'),
  fullName: nameSchema('Full Name'),
  emailId: emailSchema(),
  contactNumber: z
    .string()
    .optional()
    .or(z.literal(''))
    .superRefine((v, ctx) => {
      const r = validatePhone(v)
      if (!r.valid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: r.message ?? 'Please enter a valid Contact Number.',
        })
      }
    }),
  // Free-form plan code — validated server-side against the Plan
  // catalogue. The dropdown is populated from the live plan list at
  // render time so a Platform Admin can pick any active plan.
  planTier: z
    .string({ message: 'Plan is required.' })
    .min(1, 'Plan is required.')
    .max(50),
  // Company location — an ISO 3166-1 alpha-2 code. Required here: unlike
  // self-signup (which geo-detects from the request IP), an admin-created
  // company has no company IP to infer from, so the admin must state where
  // the organisation is based. An empty selection fails the length check.
  country: z
    .string({ message: 'Please select a Country.' })
    .length(2, 'Please select a Country.'),
})

const EMPTY = {
  organisationName: '',
  fullName: '',
  emailId: '',
  contactNumber: '',
  country: '',
  // Empty by default — the useEffect below sets it to the catalogue's
  // default plan (or the first active one) once /super-admin/plans
  // resolves. Pre-filling a literal `flowdesk_only` here would render
  // the select as empty when no Plan row with that code exists.
  planTier: '',
}

export function TenantCreateDialog({ open, onClose }) {
  const qc = useQueryClient()
  const [created, setCreated] = useState(null)

  // Live plan catalogue — drives the Plan dropdown. Cached for 5 min
  // so re-opening the modal during a session doesn't re-fetch.
  const plansQuery = useQuery({
    queryKey: ['platform-plans-dropdown'],
    enabled: open,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await api.get('/super-admin/plans')
      return plansResponse.parse(res.data)
    },
  })
  const planOptions = useMemo(() => {
    const list = plansQuery.data?.data ?? []
    return [...list]
      .filter((p) => p.isActive)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
      .map((p) => ({ value: p.code, label: p.name }))
  }, [plansQuery.data])

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: EMPTY,
    // Give immediate feedback: validate a field the moment it's left (blur),
    // then live on every keystroke after — so "00000" / "#$" / "123456" flag
    // an inline error while typing instead of only on submit.
    mode: 'onTouched',
  })

  useEffect(() => {
    if (open) {
      setCreated(null)
      form.reset(EMPTY)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Seed the form's planTier with the default plan's code (if any)
  // once the plan list lands. Form starts with planTier='', so the
  // placeholder option shows until this fires.
  useEffect(() => {
    if (!plansQuery.data?.data) return
    // Only auto-seed if the admin hasn't already picked something —
    // re-running the effect on a later refetch shouldn't yank their
    // selection back to the default.
    if (form.getValues('planTier')) return
    const def =
      plansQuery.data.data.find((p) => p.isDefault && p.isActive) ??
      plansQuery.data.data.find((p) => p.isActive) ??
      null
    if (def) {
      form.setValue('planTier', def.code)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plansQuery.data])

  const createMutation = useMutation({
    mutationFn: async (v) => {
      const res = await api.post('/super-admin/tenants', {
        organisationName: v.organisationName.trim(),
        ownerFullName: v.fullName.trim(),
        ownerEmail: v.emailId.trim().toLowerCase(),
        ownerContactNumber: v.contactNumber?.trim() || null,
        planTier: v.planTier,
        country: v.country,
      })
      return res.data
    },
    onSuccess: (data) => {
      toast.success(`"${data.name}" added successfully.`)
      void qc.invalidateQueries({ queryKey: ['platform-tenants'] })
      void qc.invalidateQueries({ queryKey: ['platform-overview'] })
      setCreated(data)
    },
    onError: (err) => {
      const { message, fields } = extractApiError(err)
      // Backend uses the `owner*` namespace for owner fields — map them
      // back onto the form's flat shape so the per-field error lands
      // under the right input.
      if (fields?.organisationName) {
        form.setError('organisationName', { message: fields.organisationName })
      } else if (fields?.ownerEmail) {
        form.setError('emailId', { message: fields.ownerEmail })
      } else if (fields?.ownerFullName) {
        form.setError('fullName', { message: fields.ownerFullName })
      } else if (fields?.ownerContactNumber) {
        form.setError('contactNumber', { message: fields.ownerContactNumber })
      } else if (fields?.country) {
        form.setError('country', { message: fields.country })
      } else {
        toast.error(message)
      }
    },
  })

  // Success state — created tenant + setupUrl returned. The admin
  // confirms with a single button to close and return to the list.
  if (created) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        title="Company Created"
        description={`"${created.name}" is now on the platform.`}
        size="md"
        footer={<Button onClick={onClose}>Done</Button>}
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 rounded-md border border-[color:var(--color-success-border)] bg-[color:var(--color-success-bg)]">
            <CheckCircle2
              size={18}
              className="text-[color:var(--color-success)] shrink-0 mt-0.5"
            />
            <div className="text-sm text-[color:var(--color-ink)]">
              We've emailed a password-setup link to{' '}
              <strong>{created.owner?.emailId}</strong>.{' '}
              {/* Window comes from the API (`formatValidityWindow` over the
                *  same constant that mints the token) — this used to say a
                *  hardcoded "7 days" while the link actually died in 60
                *  minutes, and the email stated a third figure. */}
              {created.setupLinkExpiresIn
                ? `The link expires in ${created.setupLinkExpiresIn} and can only be used once.`
                : 'The link can only be used once.'}
            </div>
          </div>
          <div>
            <p className="mb-1.5 text-sm font-semibold text-[color:var(--color-ink-strong)]">
              Setup link (fallback)
            </p>
            <p className="mb-2 text-xs text-[color:var(--color-ink-muted)]">
              If the email doesn't arrive, copy this link and share it
              directly with the owner.
            </p>
            <CopyableUrl url={created.setupUrl} />
          </div>
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create Company"
      description="The owner you nominate becomes this company's Company Admin."
      size="md"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={createMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={form.handleSubmit((v) => createMutation.mutate(v))}
            loading={createMutation.isPending}
          >
            Create Company
          </Button>
        </>
      }
    >
      <form
        onSubmit={form.handleSubmit((v) => createMutation.mutate(v))}
        className="space-y-4"
        noValidate
      >
        <TextField
          label="Company Name"
          required
          autoComplete="organization"
          placeholder="Enter a Company Name"
          error={form.formState.errors.organisationName?.message}
          {...form.register('organisationName')}
        />
        <TextField
          label="Full Name"
          required
          autoComplete="name"
          placeholder="Enter the Full Name"
          error={form.formState.errors.fullName?.message}
          {...form.register('fullName')}
        />
        <TextField
          label="Email ID"
          required
          type="email"
          autoComplete="email"
          placeholder="Enter the Email ID"
          error={form.formState.errors.emailId?.message}
          {...form.register('emailId')}
        />
        <PhoneInputField
          label="Contact Number"
          id="tenant-contactNumber"
          value={form.watch('contactNumber') ?? ''}
          onChange={(v) =>
            form.setValue('contactNumber', v, { shouldValidate: true })
          }
          error={form.formState.errors.contactNumber?.message}
          // Country ⇄ phone sync: picking a Country below re-points the phone's
          // flag + dial code (keeping any national digits already typed), and
          // changing the phone's own flag fills the Country field back. The
          // value re-point lives in PhoneInputField's `country` effect so the
          // flag actually moves even when a number is already entered.
          country={form.watch('country') || undefined}
          onCountryChange={(code) =>
            form.setValue('country', code, { shouldValidate: true })
          }
        />
        <div>
          <label
            htmlFor="country"
            className="block w-fit mb-1.5 text-[14px] font-medium text-[color:var(--color-ink-strong)]"
          >
            Country
            <span
              aria-hidden="true"
              className="ml-0.5 text-[color:var(--color-priority-urgent)]"
            >
              *
            </span>
            <span className="sr-only">required</span>
          </label>
          {/* Searchable country picker (flags + type-to-search). Required,
           *  so no "clear" row (clearLabel={null}). Wired to react-hook-form
           *  via watch/setValue since it's a controlled popover, not a
           *  native <select>. */}
          <CountryFilter
            id="country"
            fullWidth
            portal
            placeholder="Select a Country"
            clearLabel={null}
            value={form.watch('country') ?? ''}
            onChange={(v) =>
              form.setValue('country', v, { shouldValidate: true })
            }
          />
          {form.formState.errors.country?.message ? (
            <p className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
              {form.formState.errors.country.message}
            </p>
          ) : null}
        </div>
        <div>
          <label
            htmlFor="planTier"
            className="block w-fit mb-1.5 text-[14px] font-medium text-[color:var(--color-ink-strong)]"
          >
            Plan
            <span
              aria-hidden="true"
              className="ml-0.5 text-[color:var(--color-priority-urgent)]"
            >
              *
            </span>
            <span className="sr-only">required</span>
          </label>
          <Select id="planTier" {...form.register('planTier')}>
            {/* Placeholder — first option, disabled, mirrors the
             *  "All statuses" / "All plans" placeholder pattern used
             *  by every other admin dropdown. Falls through to the
             *  real default plan once the catalogue resolves. */}
            <option value="" disabled>
              {plansQuery.isLoading
                ? 'Loading plans…'
                : planOptions.length === 0
                  ? 'No active plans available'
                  : 'Select a Plan'}
            </option>
            {planOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
          {form.formState.errors.planTier?.message ? (
            <p className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
              {form.formState.errors.planTier.message}
            </p>
          ) : null}
        </div>
      </form>
    </Modal>
  )
}
